import React, { useState, useEffect } from 'react';
import { LuCreditCard, LuEye, LuEyeOff } from 'react-icons/lu';
import AppButton from '@/components/atoms/AppButton';
import { Checkbox, Avatar } from '@material-tailwind/react';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { copyToClipboard } from '@/utils/copyToClipboard';
import Tooltip from '@/components/atoms/Tooltip';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import Pagination from '@/components/organism/Table/Pagination';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import { isEmpty } from 'lodash';
import { getQuickPayMasterProducts } from '@/store/slices/quickPay.slice';
import { QuickPayMasterProduct } from '@/store/types';
import { getQuickPayMasterProductList } from '@/store/selectors/quickPay.selector';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import {
  applyTimeZoneToSelectedCustomDate,
  getDateTime,
  getSelectedTimeZone,
} from '@/utils/dateTime';
import {
  pagination,
  STRINGS,
  ROW_PER_PAGE_OPTION_LARGE,
  SELECTED_TIMEZONE,
  ISO_FORMAT,
  REPORT_FILE_NAMES,
  MODAL_TITLES,
} from '@/constant/global/global.constant';
import { QUICKPAY_PROPS } from '@/constant/quickPay/quickPay.constant';
import {
  EReportFormat,
  EReportLabel,
} from '@/constant/downloadReport/downloadReport.const';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleFill,
} from 'react-icons/ri';
import { trimSpacesInPayload } from '@/utils/stringFormatting';
import useDeepEffect from '@/hooks/useDeepEffect';
import { EExportPage } from '@/components/organism/ExportDataModal/export.types';
import { useExportFormats } from '@/hooks/useExportFormats';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import brokenImage from '../../assets/images/No-Image-Available.png';

// Update IColumnType to accept an optional isExpanded argument in render
interface IColumnType<T> {
  accessor: keyof T | string;
  header: string;
  render?: (
    column: IColumnType<T>,
    item: T,
    isExpanded?: boolean,
    rowIndex?: number,
  ) => React.ReactNode;
  sorting?: boolean;
}

const tableHeaderContent = (
  isCopy: boolean,
  onCopyClick: () => void,
  expandedRow: number | null,
  onhandleClick: (rowIndex: number) => void,
) => {
  const columns: IColumnType<QuickPayMasterProduct>[] = [
    {
      accessor: 'pepsiProductId',
      header: QUICKPAY_PROPS.COLUMNS.PEPSI_PRODUCT_ID,
      sorting: true,
      render: (_, item) => (
        <Tooltip id="provider_id_tooltip" showToolTips={isCopy} label="Copy">
          <Tooltip content={item?.productId} placement="right">
            <span
              className="cursor-pointer hover:text-primary transition-colors overflow-hidden whitespace-nowrap w-[120px] inline-block text-ellipsis"
              onClick={() => {
                copyToClipboard(item?.pepsiProductId);
                onCopyClick();
              }}
            >
              {item?.pepsiProductId}
            </span>
          </Tooltip>
        </Tooltip>
      ),
    },
    {
      accessor: 'name',
      header: QUICKPAY_PROPS.COLUMNS.PRODUCT_NAME,
      sorting: true,
      render: (_, item) => (
        <Tooltip id="device_id_tooltip" showToolTips={isCopy} label="Copy">
          <Tooltip content={item?.name} placement="right">
            <span
              className="cursor-pointer hover:text-primary transition-colors overflow-hidden whitespace-nowrap min-w-[150px] inline-block text-ellipsis"
              onClick={() => {
                copyToClipboard(item?.name);
                onCopyClick();
              }}
            >
              {item?.name}
            </span>
          </Tooltip>
        </Tooltip>
      ),
    },
    {
      accessor: 'imageUrl',
      header: QUICKPAY_PROPS.COLUMNS.PRODUCT_IMAGE,
      render: (_, item) => (
        <Avatar
          src={item?.imageUrl}
          alt="avatar"
          variant="rounded"
          size="xl"
          className="object-contain hover-zoom-in-3x cursor-pointer"
          placeholder=""
          onResize={undefined}
          onResizeCapture={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            (e.target as HTMLImageElement).src = brokenImage;
          }}
        />
      ),
    },
    {
      accessor: 'deletedOn',
      header: QUICKPAY_PROPS.COLUMNS.ACTIVE,
      render: (_, item) => (
        <div className="relative group">
          {item?.deletedOn && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs p-2 rounded shadow-lg whitespace-nowrap z-10">
              {QUICKPAY_PROPS.TABLE.DELETED_ON_PREFIX}
              {getDateTime(item?.deletedOn)}
            </div>
          )}
        </div>
      ),
    },
    {
      accessor: 'lastChangeDtm',
      header: QUICKPAY_PROPS.COLUMNS.LAST_CHANGE_DTM,
      render: (_, { lastChangeDtm }) => (
        <span>{getDateTime(lastChangeDtm)}</span>
      ),
    },
    {
      accessor: 'updatedOn',
      header: QUICKPAY_PROPS.COLUMNS.UPDATED_ON,
      sorting: true,
      render: (_, { updatedOn }) => <span>{getDateTime(updatedOn)}</span>,
    },
    {
      accessor: 'createdOn',
      header: QUICKPAY_PROPS.COLUMNS.CREATED_ON,
      sorting: true,
      render: (_, { createdOn }) => <span>{getDateTime(createdOn)}</span>,
    },
    {
      accessor: 'action',
      header: QUICKPAY_PROPS.COLUMNS.ACTION,
      render: (_, item, isExpanded, rowIndex) => (
        <AppButton
          variant="info"
          tint
          size="xs"
          hoverBg={false}
          icon={
            isExpanded ? (
              <LuEyeOff className="w-3 h-3" />
            ) : (
              <LuEye className="w-3 h-3" />
            )
          }
          onClick={() => onhandleClick(rowIndex!)}
        >
          {isExpanded
            ? QUICKPAY_PROPS.TABLE.HIDE_DETAIL
            : QUICKPAY_PROPS.TABLE.VIEW_DETAIL}
        </AppButton>
      ),
    },
  ];
  return columns;
};

const QuickPay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [createdStartDate, setStartDate] = useState<string>('');
  const [createdEndDate, setStartEndDate] = useState<string>('');
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>(false);
  const [isStateMutated, setIsStateMutated] = useState(false);
  const [searchByProductName, onSearchByProductName] = useState('');
  const [barcode, onSearchByBarcode] = useState('');
  const [productCode, onSearchByProductCode] = useState('');
  const [sorting, setSorting] = useState({
    orderBy: 'pepsiProductId',
    order: 'asc',
  });
  const selectedTimezone = getSelectedTimeZone();
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const exportFormats = useExportFormats(EExportPage.QUICK_PAY);

  const [quickPayPagination, setQuickPayPagination] = useState({
    limit: ROW_PER_PAGE_OPTION_LARGE[0],
    page: pagination.page,
  });

  const barcodeQuery = useDebounce(
    barcode,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const productCodeQuery = useDebounce(
    productCode,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const searchByProductNameQuery = useDebounce(
    searchByProductName,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  const dispatch = useDispatch();
  const toast = useToast();

  const {
    quickPayMasterProducts: { data, pagination: paginationResponse },
    loading,
  } = useSelector(getQuickPayMasterProductList);

  const totalPages = Math.ceil(
    paginationResponse?.totalRecords / quickPayPagination.limit,
  );

  const handlePageSizeChange = (value: number) => {
    setQuickPayPagination({ ...quickPayPagination, limit: value });
    if (totalPages < quickPayPagination?.page) {
      setQuickPayPagination({ page: totalPages, limit: value });
    }
  };

  const handlePreviousClick = (page: number) => {
    if (page > 1 && page <= totalPages) {
      setQuickPayPagination({ ...quickPayPagination, page: page - 1 });
    }
  };

  const handleNextClick = (page: number) => {
    if (page >= 1 && page < totalPages) {
      setQuickPayPagination({ ...quickPayPagination, page: page + 1 });
    }
  };

  const fetchQuickTransaction = async (param: any) => {
    try {
      dispatch(getQuickPayMasterProducts({ ...param }));
    } catch (error: any) {
      toast?.pushWarning(
        error?.response?.data?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
    }
  };
  const formattedStartDate =
    createdStartDate &&
    applyTimeZoneToSelectedCustomDate(
      createdStartDate,
      ISO_FORMAT,
      SELECTED_TIMEZONE,
      true,
    );

  const formattedEndDate =
    createdEndDate &&
    applyTimeZoneToSelectedCustomDate(
      createdEndDate,
      ISO_FORMAT,
      SELECTED_TIMEZONE,
      false,
    );
  const handleApply = () => {
    const param = {
      ...quickPayPagination,
      createdEndDate: formattedEndDate,
      createdStartDate: formattedStartDate,
      inactive: isDeleted,
      ...sorting,
    };
    fetchQuickTransaction(param);
    setIsOpen(false);
    setIsStateMutated(false);
  };
  const onhandleClick = (rowIndex: number) => {
    setExpandedRow((prev) => (prev === rowIndex ? null : rowIndex));
  };
  const handlereset = () => {
    setStartDate('');
    setStartEndDate('');

    const param = {
      ...quickPayPagination,
      ...sorting,
      inactive: false,
      createdStartDate: '',
      createdEndDate: '',
    };
    fetchQuickTransaction(param);
    setIsOpen(false);
    setIsDeleted(false);
    setIsStateMutated(false);
  };

  const onCopyClick = () => {
    setIsCopy(true);
    setTimeout(() => {
      setIsCopy(false);
    }, 500);
  };

  useEffect(() => {
    if (quickPayPagination?.page > totalPages) {
      setQuickPayPagination({ ...quickPayPagination, page: 1 });
    }
  }, [totalPages]);
  useDeepEffect(() => {
    setIsStateMutated(true);
  }, [
    quickPayPagination,
    barcodeQuery,
    productCodeQuery,
    searchByProductNameQuery,
    sorting,
    isDeleted,
  ]);
  useEffect(() => {
    const formattedPayload = trimSpacesInPayload({
      ...(barcodeQuery && barcodeQuery.length > 0
        ? { barcode: barcodeQuery }
        : {}),
      ...(productCodeQuery && productCodeQuery.length > 0
        ? { pepsiProductId: productCodeQuery }
        : {}),
      ...(searchByProductNameQuery && searchByProductNameQuery.length > 0
        ? { name: searchByProductNameQuery }
        : {}),
    });
    if (isStateMutated) {
      fetchQuickTransaction({
        ...quickPayPagination,
        ...sorting,
        ...formattedPayload,
        createdEndDate: formattedEndDate,
        createdStartDate: formattedStartDate,
        inactive: isDeleted,
      });
      setIsStateMutated(false);
    }
  }, [
    quickPayPagination,
    barcodeQuery,
    productCodeQuery,
    searchByProductNameQuery,
    sorting,
    isDeleted,
    isStateMutated,
  ]);
  const isApplyButtonDisabled =
    createdStartDate &&
    createdEndDate &&
    new Date(createdEndDate) >= new Date(createdStartDate);
  const isError =
    createdStartDate !== '' &&
    createdEndDate !== '' &&
    createdStartDate > createdEndDate;
  return (
    <FullPageCard
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.QUICK_PICK}
          subtitle={STRINGS.QUICK_PICK_SUB}
          icon={getNavItemIcon(NAV_ITEMS.QUICK_PAY)}
          count={paginationResponse?.totalRecords}
          actionsSlot={<div className="flex items-center gap-2" />}
          filtersRight={
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                <Checkbox
                  color="blue-gray"
                  name="deleted"
                  checked={isDeleted}
                  onChange={() => setIsDeleted(!isDeleted)}
                  className="h-4 w-4"
                  onResize={undefined}
                  onResizeCapture={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                  crossOrigin={undefined}
                />
                {QUICKPAY_PROPS.FILTERS.SHOW_DELETED}
              </label>
              <GlobalSearch
                className="w-[160px]"
                placeholder={QUICKPAY_PROPS.FILTERS.BARCODE_PLACEHOLDER}
                globalSearch={barcode}
                setGlobalSearch={onSearchByBarcode}
              />
              <GlobalSearch
                className="w-[160px]"
                placeholder={QUICKPAY_PROPS.FILTERS.PRODUCT_NAME_PLACEHOLDER}
                globalSearch={searchByProductName}
                setGlobalSearch={onSearchByProductName}
              />
              <GlobalSearch
                className="w-[180px]"
                placeholder={
                  QUICKPAY_PROPS.FILTERS.PEPSI_PRODUCT_ID_PLACEHOLDER
                }
                globalSearch={productCode}
                setGlobalSearch={onSearchByProductCode}
              />
            </div>
          }
        />
      }
      footer={
        !isEmpty(data) ? (
          <Pagination
            onRowsPerPageChange={handlePageSizeChange}
            currentPage={quickPayPagination.page}
            itemPerpage={quickPayPagination?.limit}
            totalItem={paginationResponse?.totalRecords}
            totalPages={totalPages}
            rowsPerPageOptions={ROW_PER_PAGE_OPTION_LARGE}
            onNextClick={handleNextClick}
            onPreviousClick={handlePreviousClick}
            floating={false}
          />
        ) : undefined
      }
    >
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto scrollbar-thin">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {/* table */}
      </div>
    </FullPageCard>
  );
};

export default QuickPay;
